import { gql } from '@apollo/client';

/**
 * Email campaigns (plan 4.4). API card 17.5; every request with a real response
 * is in ilbp-backend docs/CRM_CONTACT_QUERY_FRONTEND.md §31.3. Admin-only.
 * Tests and scheduling are metered with bulk tagging (`crm-bulk`, 10 a minute).
 * Figures arrive from SendGrid's webhook: nothing here polls.
 */

const LIST_FIELDS = `
  id
  source
  name
  subject
  status
  segmentId
  segmentName
  scheduledAt
  startedAt
  sentAt
  canceledAt
  recipientCount
  sentCount
  openCount
  clickCount
  bounceCount
  unsubCount
  openRate
  clickRate
  workflowStepCount
  nbCreatedAt
  createdAt
  updatedAt
`;

const FULL_FIELDS = `
  ${LIST_FIELDS}
  preheader
  fromName
  fromEmail
  bodyHtml
  bodyText
  machineOpenCount
  spamCount
`;

export const CRM_EMAIL_CAMPAIGNS = gql`
  query CrmEmailCampaigns($status: [CampaignStatus!], $search: String, $source: String, $skip: Int, $take: Int) {
    crmEmailCampaigns(status: $status, search: $search, source: $source, skip: $skip, take: $take) {
      total
      items {
        ${LIST_FIELDS}
      }
    }
  }
`;

/** The tiles above the list: four root fields, once a visit. */
export const CRM_EMAIL_TOTALS = gql`
  query CrmEmailTotals {
    drafts: crmEmailCampaigns(status: [DRAFT], source: "MANUAL", take: 1) {
      total
    }
    upcoming: crmEmailCampaigns(status: [SCHEDULED, SENDING], source: "MANUAL", take: 1) {
      total
    }
    sent: crmEmailCampaigns(status: [SENT], source: "MANUAL", take: 1) {
      total
    }
    migrated: crmEmailCampaigns(source: "NATIONBUILDER", take: 1) {
      total
    }
  }
`;

export const CRM_EMAIL_CAMPAIGN = gql`
  query CrmEmailCampaign($id: Int!) {
    crmEmailCampaign(id: $id) {
      ${FULL_FIELDS}
    }
  }
`;

export const CRM_CREATE_EMAIL_CAMPAIGN = gql`
  mutation CrmCreateEmailCampaign($input: CrmEmailCampaignInput!) {
    crmCreateEmailCampaign(input: $input) {
      ${FULL_FIELDS}
    }
  }
`;

export const CRM_UPDATE_EMAIL_CAMPAIGN = gql`
  mutation CrmUpdateEmailCampaign($id: Int!, $input: CrmEmailCampaignInput!) {
    crmUpdateEmailCampaign(id: $id, input: $input) {
      ${FULL_FIELDS}
    }
  }
`;

export const CRM_DUPLICATE_EMAIL_CAMPAIGN = gql`
  mutation CrmDuplicateEmailCampaign($id: Int!) {
    crmDuplicateEmailCampaign(id: $id) {
      id
      name
      status
    }
  }
`;

export const CRM_DELETE_EMAIL_CAMPAIGN = gql`
  mutation CrmDeleteEmailCampaign($id: Int!) {
    crmDeleteEmailCampaign(id: $id)
  }
`;

/** Who a list or saved filter reaches: `canEmail` is the number it sends to, and the only one to confirm. */
export const CRM_EMAIL_AUDIENCE = gql`
  query CrmEmailAudience($segmentId: Int!) {
    crmEmailAudience(segmentId: $segmentId) {
      total
      withEmail
      emailOptIn
      unsubscribed
      emailIsBad
      doNotContact
      canEmail
    }
  }
`;

export const CRM_SEND_TEST_EMAIL = gql`
  mutation CrmSendTestEmail($id: Int!, $to: [String!]!) {
    crmSendTestEmail(id: $id, to: $to) {
      sentTo
      dryRun
    }
  }
`;

/** A short fixed "[Test]" email with no campaign: does email from the CRM arrive? */
export const CRM_SEND_DELIVERY_TEST = gql`
  mutation CrmSendDeliveryTest($to: [String!]!) {
    crmSendDeliveryTest(to: $to) {
      sentTo
      dryRun
    }
  }
`;

/** Our campaigns of any status, as typed, for a test of one. */
export const CRM_TEST_CAMPAIGN_CHOICES = gql`
  query CrmTestCampaignChoices($search: String) {
    crmEmailCampaigns(source: "MANUAL", search: $search, take: 50) {
      items {
        id
        name
        subject
      }
    }
  }
`;

export const CRM_SCHEDULE_EMAIL_CAMPAIGN = gql`
  mutation CrmScheduleEmailCampaign($id: Int!, $expectedCount: Int!, $sendAt: DateTime) {
    crmScheduleEmailCampaign(id: $id, expectedCount: $expectedCount, sendAt: $sendAt) {
      ${FULL_FIELDS}
    }
  }
`;

export const CRM_UNSCHEDULE_EMAIL_CAMPAIGN = gql`
  mutation CrmUnscheduleEmailCampaign($id: Int!) {
    crmUnscheduleEmailCampaign(id: $id) {
      ${FULL_FIELDS}
    }
  }
`;

export const CRM_CANCEL_EMAIL_CAMPAIGN = gql`
  mutation CrmCancelEmailCampaign($id: Int!) {
    crmCancelEmailCampaign(id: $id) {
      ${FULL_FIELDS}
    }
  }
`;

export const CRM_EMAIL_CAMPAIGN_SENDS = gql`
  query CrmEmailCampaignSends($campaignId: Int!, $status: EmailSendStatus, $skip: Int, $take: Int) {
    crmEmailCampaignSends(campaignId: $campaignId, status: $status, skip: $skip, take: $take) {
      total
      items {
        id
        contactId
        contactName
        email
        status
        skipReason
        sentAt
        deliveredAt
        openedAt
        clickedAt
        bouncedAt
        unsubscribedAt
        spamReportedAt
      }
    }
  }
`;
